import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Dialog,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  TablePagination,
} from "@mui/material";
import {
  useGetAllPolicyGroupByQuery,
  useGetCategoryQuery,
  useGetProductBrandQuery,
  useGetProductQuery,
  useGetCustomerQuery,
  useGetAllPolicyFilterQuery,
  useGetAllCustomerRelationQuery,
} from "../../../Api/attoDeskApi";
import {
  ICategory,
  ICustomer,
  ICustomerRelative2,
  IPolicy,
  IProductBrand,
  IProductPopUP,
} from "../../../Api/Interface/api.interface";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import TableViewIcon from "@mui/icons-material/TableView";
import * as XLSX from "xlsx";
import {
  DataGrid,
  GridColDef,
  GridPagination,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbar,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { formatNumber } from "../../../Components/formatNumber/formatNumber";

interface CustomerOption {
  label: string;
  value: number;
}

const allColumns = [
  "Policy No",
  "Type of Coverage",
  "Insurance Company",
  "Product Description",
  "Policy Owner",
  "Joint Policy Owner",
  "Contingent Policy Owner",
  "Insured",
  "Payor",
  "Primary Beneficiaries",
  "Contingent Beneficiaries",
  "Coverage Amount",
  "Premium Details",
  "Premium",
  "Premium Frequency",
  "Draw Day",
  "Issue Date",
  "Expiry Date",
  "Conversion Date",
  "Renewal Date",
  "Paid Up Date",
  "Remark",
];

const policyColumns = [
  "Policy No",
  "Insurance Company",
  "Policy Owner",
  "Premium",
  "Premium Frequency",
  "Draw Day",
  "Issue Date",
];

const coverageColumns = allColumns.filter(
  (col) => !policyColumns.includes(col)
);

const stripHtml = (html: string | null | undefined): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | "">("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [rows, setRows] = useState<IPolicy[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>(
    []
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [includeCoverageColumns, setIncludeCoverageColumns] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });

  const handleOpen = () => {
    setSelectedColumns(policyColumns);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const { data: staffData, isLoading: staffIsLoading } = useGetCustomerQuery({
    searchText: "",
    id: "",
  });

  const { data, isLoading, isError, refetch } = useGetAllPolicyGroupByQuery({
    searchText: currentSearchQuery,
    id: "",
    policyCustomerId: selectedCustomerId,
  });

  const {
    data: relativeData,
    isLoading: relativeIsLoading,
    isError: relativeError,
  } = useGetAllCustomerRelationQuery({
    policyCustomerId: "",
  });

  const {
    data: coverageGetAllData,
    isLoading: coverageIsLoading,
    isError: coverageIsError,
  } = useGetAllPolicyFilterQuery({
    searchText: currentSearchQuery,
    policyCustomerId: selectedCustomerId,
    createdDateStart: null,
    createdDateEnd: null,
    createdDate: null,
    policyCategoryId: "",
    policyInsuranceCompanyId: "",
    policyProductId: "",
    contengentPolicyOwnerId: "",
    jointPolicyOwnerId: "",
    policyIssueDate: "",
    policyRenewalDate: null,
    policyPaidUpDate: null,
    policyExpiryDate: null,
    policyCoversionDate: null,
    policyIsLifeTimeExpiryDate: null,
    policyPayor: "",
  });

  const { data: coursingData, isLoading: coursingLoading } =
    useGetCategoryQuery();

  const {
    data: productData,
    isLoading: productDataIsLoading,
    isError: productDataError,
  } = useGetProductQuery({
    searchText: "",
    id: "",
    productBrandId: null,
    productCategoryId: "",
    productTagIds: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    createdDate: null,
  });

  const { data: customerDate, isLoading: customerIsLoading } =
    useGetCustomerQuery({
      searchText: "",
      id: "",
    });

  const { data: brandListData, isLoading: brandIsLoading } =
    useGetProductBrandQuery({
      searchText: "",
      isActive: true,
    });

  // const handleChangePage = (event: unknown, newPage: number) => {
  //   setPage(newPage);
  // };

  const coursingList = useMemo(() => {
    return coursingData?.data as ICategory[];
  }, [coursingData?.data]);

  const productList = useMemo(() => {
    return productData?.data as IProductPopUP[];
  }, [productData?.data]);

  const customerList = useMemo(() => {
    return customerDate?.data as ICustomer[];
  }, [customerDate?.data]);

  const brandList = useMemo(() => {
    return brandListData?.data as IProductBrand[];
  }, [brandListData?.data]);

  const relationDataList = useMemo(() => {
    return (relativeData?.data ?? []) as ICustomerRelative2[];
  }, [relativeData?.data]);

  const coursingMap = useMemo(() => {
    const map = new Map();
    coursingList?.forEach((category) => {
      map.set(category.id, category.categoryName);
    });
    return map;
  }, [coursingList]);

  const cutomerMap = useMemo(() => {
    const map = new Map();
    customerList?.forEach((category) => {
      map.set(
        category.id,
        toProperCase(category.customerFirstName || "") +
          " " +
          toProperCase(category.customerLastName || "")
      );
    });
    return map;
  }, [customerList]);

  const productMap = useMemo(() => {
    const map = new Map();
    productList?.forEach((category) => {
      map.set(category.id, category.productName);
    });
    return map;
  }, [productList]);

  const brandMap = useMemo(() => {
    const map = new Map();
    brandList?.forEach((category) => {
      map.set(category.id, category.productBrandName);
    });
    return map;
  }, [brandList]);

  const relativeMap = useMemo(() => {
    const map = new Map();
    relationDataList?.forEach((item) => {
      map.set(item.id, `${toProperCase(item?.related_customer_name)}`);
    });
    return map;
  }, [relationDataList]);

  const cliendList = useMemo(() => {
    return (staffData?.data ?? []) as ICustomer[];
  }, [staffData?.data]);

  const customerOptions = useMemo(() => {
    return cliendList.map((cust) => ({
      label: `${cust.customerFirstName} ${cust.customerLastName}`,
      value: cust.id,
    }));
  }, [cliendList]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleCheckboxChange = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column]
    );
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setSelectedColumns(allColumns);
    } else {
      setSelectedColumns([]);
    }
  };

  // const handleToggleCoverage = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   setIncludeCoverageColumns(e.target.checked);
  // };

  const handleToggleCoverage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIncludeCoverageColumns(checked);

    if (checked) {
      // Add only missing coverage columns to the selected list
      setSelectedColumns((prev) =>
        prev.concat(coverageColumns.filter((col) => !prev.includes(col)))
      );
    } else {
      // Remove only coverage columns from the selected list
      setSelectedColumns((prev) =>
        prev.filter((col) => !coverageColumns.includes(col))
      );
    }
  };

  const isAllPolicySelected = policyColumns.every((col) =>
    selectedColumns.includes(col)
  );

  const isAllCoverageSelected = coverageColumns.every((col) =>
    selectedColumns.includes(col)
  );

  const handleSelectAllPolicy = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedColumns((prev) =>
        prev.concat(policyColumns.filter((col) => !prev.includes(col)))
      );
    } else {
      setSelectedColumns((prev) =>
        prev.filter((col) => !policyColumns.includes(col))
      );
    }
  };

  const handleSelectAllCoverage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedColumns((prev) =>
        prev.concat(coverageColumns.filter((col) => !prev.includes(col)))
      );
    } else {
      setSelectedColumns((prev) =>
        prev.filter((col) => !coverageColumns.includes(col))
      );
    }
  };

  const coursings: IPolicy[] = Array.isArray(data?.data)
    ? (data?.data as IPolicy[])
    : [];

  const columns = [
    {
      field: "policyNumber",
      headerName: "Policy No",
      flex: 1,
      // headerClassName: classes.tablheader200,
      // cellClassName: classes.tablCell,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          to={`/customer/${params.row.policyCustomerId}/policy/${params.row.policyNumber}`}
          className={classes.iconLinkPropNormal}
        >
          <span
            style={{
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
            // onClick={() =>
            //   navigate(
            //     `/customer/${params.row.policyCustomerId}/policy/${params.row.policyNumber}`
            //   )
            // }
          >
            {params.value}
            {params.value && (
              <FontAwesomeIcon icon={faLink} style={{ color: "#1976d2" }} />
            )}
          </span>
        </Link>
      ),
    },
    {
      field: "insuranceCompany",
      headerName: "Insurance Company",
      flex: 1,
      // headerClassName: classes.tablheader200,
      // cellClassName: classes.tablCell,
      minWidth: 200,
    },
    {
      field: "policyOwner",
      headerName: "Policy Owner",
      flex: 1,
      // headerClassName: classes.tablheader200,
      // cellClassName: classes.tablCell,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          to={`/profile/customer/${params.row.policyCustomerId}`}
          className={classes.iconLinkPropNormal}
        >
          <span
            style={{
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
            // onClick={() =>
            //   navigate(`/profile/customer/${params.row.policyCustomerId}`)
            // }
          >
            {params.value}
            {params.value && (
              <FontAwesomeIcon icon={faUser} style={{ color: "#1976d2" }} />
            )}
          </span>
        </Link>
      ),
    },
    {
      field: "policyPremium",
      headerName: "Premium",
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.value ? `${formatNumber(params.value)}` : ""}</div>
      ),
    },
    {
      field: "policyPremiumFrequency",
      headerName: "Premium Frequency",
      minWidth: 200,
    },
    { field: "policyDrawDay", headerName: "Draw Day", minWidth: 200 },
    {
      field: "policyIssueDate",
      headerName: "Issue Date",
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? dayjs(params.value).format("MMM D, YYYY") : "";
      },
    },
  ];

  const formattedRows = useMemo(() => {
    return coursings.map((row) => ({
      id: row.policyNumber,
      policyNumber: row.policyNumber,
      insuranceCompany:
        brandMap.get(Number(row.policyInsuranceCompanyId)) || "",
      policyPremium: row.policyPremium,
      policyPremiumFrequency: row.policyPremiumFrequency,
      policyDrawDay: row.policyDrawDay,
      policyIssueDate: row.policyIssueDate,
      policyOwner: cutomerMap.get(Number(row.policyCustomerId)) || "",
      policyCustomerId: row.policyCustomerId,
    }));
  }, [coursings, brandMap, cutomerMap]);

  const handleRowSelection = (selectionModel: GridRowSelectionModel) => {
    setSelectedRowIds(selectionModel);
  };

  const handleExport = () => {
    if (includeCoverageColumns) {
      handleExportExcelWithCoverage(selectedColumns);
    } else {
      handleExportExcel(selectedColumns);
    }
    handleClose();
  };

  const handleExportExcel = (selectedCols: string[]) => {
    const coursings: IPolicy[] = Array.isArray(data?.data)
      ? (data?.data as IPolicy[])
      : [];

    const selectedRows = coursings.filter((row) =>
      selectedRowIds.includes(row.policyNumber)
    );

    if (selectedRows.length === 0) {
      showErrorMessage("Please select at least one row to export!");
      return;
    }
    const fullExcelData = selectedRows.map((row) => ({
      "Policy No": row.policyNumber,
      "Type of Coverage": coursingMap.get(Number(row.policyCategoryId)) || "",
      "Insurance Company":
        brandMap.get(Number(row.policyInsuranceCompanyId)) || "",
      "Product Description": productMap.get(Number(row.policyProductId)) || "",
      "Policy Owner": cutomerMap.get(Number(row.policyCustomerId)) || "",
      "Joint Policy Owner":
        relativeMap.get(Number(row.jointPolicyOwnerId)) || "",
      "Contingent Policy Owner":
        relativeMap.get(Number(row.contengentPolicyOwnerId)) || "",
      Insured: (row.policyInsuredDetails || [])
        .map((insuredId) => relativeMap.get(Number(insuredId)) || "")
        .join(", "),
      Payor: toProperCase(row.policyPayor),
      "Primary Beneficiaries": row.policyPrimaryBenificariesNew
        .map((insured) => {
          const name = relativeMap.get(Number(insured.column1)) || "";
          const payout = insured.payoutPercentage
            ? ` - ${insured.payoutPercentage}%`
            : "";
          const type = insured.column2 ? ` (${insured.column2})` : "";
          return `${name}${payout}${type}`;
        })
        .join(", "),
      "Contingent Beneficiaries": row.policyContengentBenificariesNew
        .map((insured) => {
          const name = relativeMap.get(Number(insured.column1)) || "";
          const payout = insured.payoutPercentage
            ? ` - ${insured.payoutPercentage}%`
            : "";
          return `${name}${payout}`;
        })
        .join(", "),
      "Coverage Amount": formatNumber(row.policyCoverageAmount),
      "Premium Details": formatNumber(row.policyPremiumDetails),
      Premium: formatNumber(row.policyPremium),
      "Premium Frequency": row.policyPremiumFrequency,
      "Draw Day": row.policyDrawDay,
      "Issue Date": row.policyIssueDate
        ? dayjs(row.policyIssueDate).format("MMM D, YYYY")
        : "",
      "Expiry Date": row.policyIsLifeTimeExpiryDate
        ? "Death"
        : row.policyExpiryDate
        ? dayjs(row.policyExpiryDate).format("MMM D, YYYY")
        : "",
      "Conversion Date": row.policyCoversionDate
        ? dayjs(row.policyCoversionDate).format("MMM D, YYYY")
        : "",
      "Renewal Date": row.policyRenewalDate
        ? dayjs(row.policyRenewalDate).format("MMM D, YYYY")
        : "",
      "Paid Up Date": row.policyPaidUpDate
        ? dayjs(row.policyPaidUpDate).format("MMM D, YYYY")
        : "",
      Remark: stripHtml(row.policyRemark),
    }));

    const filteredExcelData = fullExcelData.map((row) => {
      const filteredRow: any = {};
      selectedCols.forEach((col) => {
        filteredRow[col] = row[col as keyof typeof row];
      });
      return filteredRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredExcelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Policies");
    const today = dayjs();
    const formattedDate = today.format("MMM, DD YYYY (dddd)");
    const filename = `Policies_${formattedDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const handleExportExcelWithCoverage = (selectedCols: string[]) => {
    const coursings: IPolicy[] = Array.isArray(coverageGetAllData?.data)
      ? (coverageGetAllData?.data as IPolicy[])
      : [];

    const selectedRows = coursings.filter((row) =>
      selectedRowIds.includes(row.policyNumber)
    );

    if (selectedRows.length === 0) {
      alert("Please select at least one row to export!");
      return;
    }
    const fullExcelData = selectedRows.map((row) => ({
      "Policy No": row.policyNumber,
      "Type of Coverage": coursingMap.get(Number(row.policyCategoryId)) || "",
      "Insurance Company":
        brandMap.get(Number(row.policyInsuranceCompanyId)) || "",
      "Product Description": productMap.get(Number(row.policyProductId)) || "",
      "Policy Owner": cutomerMap.get(Number(row.policyCustomerId)) || "",
      "Joint Policy Owner":
        relativeMap.get(Number(row.jointPolicyOwnerId)) || "",
      "Contingent Policy Owner":
        relativeMap.get(Number(row.contengentPolicyOwnerId)) || "",
      Insured: (row.policyInsuredDetails || [])
        .map((insuredId) => relativeMap.get(Number(insuredId)) || "")
        .join(", "),
      Payor: toProperCase(row.policyPayor),
      "Primary Beneficiaries": row.policyPrimaryBenificariesNew
        .map((insured) => {
          const name = relativeMap.get(Number(insured.column1)) || "";
          const payout = insured.payoutPercentage
            ? ` - ${insured.payoutPercentage}%`
            : "";
          const type = insured.column2 ? ` (${insured.column2})` : "";
          return `${name}${payout}${type}`;
        })
        .join(", "),
      "Contingent Beneficiaries": row.policyContengentBenificariesNew
        .map((insured) => {
          const name = relativeMap.get(Number(insured.column1)) || "";
          const payout = insured.payoutPercentage
            ? ` - ${insured.payoutPercentage}%`
            : "";
          return `${name}${payout}`;
        })
        .join(", "),
      "Coverage Amount": formatNumber(row.policyCoverageAmount),
      "Premium Details": row.policyPremiumDetails,
      Premium: formatNumber(row.policyPremium),
      "Premium Frequency": row.policyPremiumFrequency,
      "Draw Day": row.policyDrawDay,
      "Issue Date": row.policyIssueDate
        ? dayjs(row.policyIssueDate).format("MMM D, YYYY")
        : "",
      "Expiry Date": row.policyIsLifeTimeExpiryDate
        ? "Death"
        : row.policyExpiryDate
        ? dayjs(row.policyExpiryDate).format("MMM D, YYYY")
        : "",
      "Conversion Date": row.policyCoversionDate
        ? dayjs(row.policyCoversionDate).format("MMM D, YYYY")
        : "",
      "Renewal Date": row.policyRenewalDate
        ? dayjs(row.policyRenewalDate).format("MMM D, YYYY")
        : "",
      "Paid Up Date": row.policyPaidUpDate
        ? dayjs(row.policyPaidUpDate).format("MMM D, YYYY")
        : "",
      Remark: stripHtml(row.policyRemark),
    }));

    const filteredExcelData = fullExcelData.map((row) => {
      const filteredRow: any = {};
      selectedCols.forEach((col) => {
        filteredRow[col] = row[col as keyof typeof row];
      });
      return filteredRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredExcelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Policies_Report");
    const today = dayjs();
    const formattedDate = today.format("MMM, DD YYYY (dddd)");
    const filename = `Policies_With_Coverage_Reports_${formattedDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const isAllSelected = selectedColumns.length === allColumns.length;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value, 10);
    setPaginationModel({ page: 0, pageSize: value });
  };

  if (isLoading || coverageIsLoading || relativeIsLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={5}
        sx={{ borderBottom: 2, padding: 2 }}
        className={classes.titleGrid}
      >
        {/* <Grid item lg={4} md={4} sm={4} xs={4} pt={2} alignContent={"center"}>
          <Typography className={classes.cardTitle}>Policy List</Typography>
        </Grid> */}
        <Grid
          item
          lg={12}
          md={12}
          sm={12}
          xs={12}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Grid
            container
            spacing={1}
            justifyContent="flex-end"
            sx={{ display: "flex" }}
          >
            <Grid item lg={3} md={3} sm={12} xs={12}>
              <Autocomplete
                options={customerOptions}
                loading={staffIsLoading}
                getOptionLabel={(option: CustomerOption) => option.label}
                onChange={(event, newValue: CustomerOption | null) => {
                  setSelectedCustomerId(String(newValue?.value ?? ""));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputLabelProps={{ shrink: true }}
                    placeholder="Policy Owner name .."
                  />
                )}
                sx={{ flexGrow: 1, width: "100%" }}
                size="small"
                className={classes.searchTextField}
              />
            </Grid>
            <Grid item>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: {
                    root: classes.formControlList,
                  },
                }}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Policy Number .."
                sx={{ width: "100%" }}
                size="small"
                className={classes.searchTextField}
              />
            </Grid>
            <Grid item>
              <Button
                // variant="contained"
                startIcon={<TableViewIcon />}
                className={classes.addNewButton}
                onClick={handleOpen}
                disabled={selectedRowIds.length === 0}
              >
                Export to Excel
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container>
        {/* <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader200}>
                  <strong>Policy No</strong>
                </TableCell>
                <TableCell className={classes.tablheader200}>
                  <strong>Insurance Company</strong>
                </TableCell>
                <TableCell className={classes.tablheader250}>
                  <strong>Policy Owner</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError || coursings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                coursings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: IPolicy, index) => (
                    <TableRow key={row.id} className={classes.tableRowHover}>
                      
                      <TableCell
                        component="th"
                        scope="row"
                        className={classes.tablCell}
                      >
                        <Typography variant="subtitle1">
                          <span
                            className={classes.customerDetails}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "#00bcd4",
                            }}
                            onClick={() =>
                              navigate(
                                `/customer/${row.policyCustomerId}/policy/${row.policyNumber}`
                              )
                            }
                          >
                            {row.policyNumber}
                          </span>
                        </Typography>
                      </TableCell>
                      <TableCell scope="row" className={classes.tablCell}>
                        {brandMap.get(Number(row.policyInsuranceCompanyId)) ||
                          ""}
                      </TableCell>
                      <TableCell scope="row" className={classes.tablCell}>
                        <Typography variant="subtitle1">
                          <span
                            className={classes.customerDetails}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "#00bcd4",
                            }}
                            onClick={() =>
                              navigate(
                                `/profile/customer/${row.policyCustomerId}`
                              )
                            }
                          >
                            {cutomerMap.get(Number(row.policyCustomerId)) || ""}
                          </span>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer> */}

        <Grid
          item
          sx={{ width: "100%", pb: 5, borderTop: 2, pt: 3 }}
          className={classes.titleGrid}
        >
          <div style={{ width: "100%" }}>
            <TablePagination
              component="div"
              count={formattedRows.length}
              page={paginationModel.page}
              onPageChange={handleChangePage}
              rowsPerPage={paginationModel.pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 20, 50, 100, formattedRows.length]}
            />

            <DataGrid
              rows={formattedRows.slice(
                paginationModel.page * paginationModel.pageSize,
                (paginationModel.page + 1) * paginationModel.pageSize
              )}
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              rowHeight={60}
              checkboxSelection
              disableRowSelectionOnClick
              className={classes.dataGridTable}
              onRowSelectionModelChange={handleRowSelection}
              hideFooter // <-- hide pagination/footer completely
            />
          </div>
          {/* <DataGrid
            rows={formattedRows}
            columns={columns}
            getRowId={(row) => row.id}
            autoHeight
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            style={{ height: "100%", width: "100%" }}
            className={classes.dataGridTable}
            rowHeight={60}
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={handleRowSelection}
          /> */}
        </Grid>
        {/* <Grid
          spacing={5}
          p={"18px"}
          mt={1}
          py={4}
          justifyContent={"end"}
          container
          textAlign={"center"}
          alignContent={"center"}
          alignItems={"center"}
        >
          <Grid
            sx={{
              textAlign: "end",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <Pagination
              count={coursings.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
            />
          </Grid>
        </Grid> */}
      </Grid>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-describedby="alert-dialog-description"
        classes={{ paper: "inherit" }}
        fullScreen={isMobile}
        fullWidth={isMobile}
        maxWidth="lg"
      >
        <Grid className={classes.Dialog}>
          <Grid container spacing={3}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Grid container spacing={3}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Policy Columns
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                      <Grid container spacing={3} p={"24px"}>
                        <>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isAllPolicySelected}
                                  indeterminate={
                                    selectedColumns.some((col) =>
                                      policyColumns.includes(col)
                                    ) && !isAllPolicySelected
                                  }
                                  onChange={handleSelectAllPolicy}
                                />
                              }
                              label="Select All"
                            />
                          </Grid>

                          {allColumns
                            .filter((column) =>
                              [
                                "Policy No",
                                "Insurance Company",
                                "Policy Owner",
                                "Premium",
                                "Premium Frequency",
                                "Draw Day",
                                "Issue Date",
                              ].includes(column)
                            )
                            .map((column) => (
                              <Grid
                                item
                                lg={3}
                                md={3}
                                sm={12}
                                xs={12}
                                key={column}
                              >
                                <FormControlLabel
                                  key={column}
                                  control={
                                    <Checkbox
                                      checked={selectedColumns.includes(column)}
                                      onChange={() =>
                                        handleCheckboxChange(column)
                                      }
                                    />
                                  }
                                  label={column}
                                />
                              </Grid>
                            ))}

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <FormControlLabel
                              label="Include Coverage Columns in Export"
                              control={
                                <Checkbox
                                  checked={includeCoverageColumns}
                                  onChange={handleToggleCoverage}
                                />
                              }
                            />
                          </Grid>
                        </>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
                {includeCoverageColumns && (
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Coverage Columns
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid container spacing={3} p={"24px"}>
                          <>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isAllCoverageSelected}
                                    indeterminate={
                                      selectedColumns.some((col) =>
                                        coverageColumns.includes(col)
                                      ) && !isAllCoverageSelected
                                    }
                                    onChange={handleSelectAllCoverage}
                                  />
                                }
                                label="Select All"
                              />
                            </Grid>

                            {allColumns
                              .filter((column) =>
                                [
                                  "Type of Coverage",
                                  "Product Description",
                                  "Joint Policy Owner",
                                  "Contingent Policy Owner",
                                  "Insured",
                                  "Payor",
                                  "Primary Beneficiaries",
                                  "Contingent Beneficiaries",
                                  "Coverage Amount",
                                  "Premium Details",
                                  "Expiry Date",
                                  "Conversion Date",
                                  "Renewal Date",
                                  "Paid Up Date",
                                  "Remark",
                                ].includes(column)
                              )
                              .map((column) => (
                                <Grid
                                  item
                                  lg={3}
                                  md={3}
                                  sm={12}
                                  xs={12}
                                  key={column}
                                >
                                  <FormControlLabel
                                    key={column}
                                    control={
                                      <Checkbox
                                        checked={selectedColumns.includes(
                                          column
                                        )}
                                        onChange={() =>
                                          handleCheckboxChange(column)
                                        }
                                      />
                                    }
                                    label={column}
                                  />
                                </Grid>
                              ))}
                          </>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                )}
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={handleClose}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<TableViewIcon />}
                          className={classes.addButton}
                          onClick={handleExport}
                          disabled={selectedColumns.length === 0}
                        >
                          Export
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Dialog>
    </Card>
  );
};

export default Index;
